import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, PlusCircle, CalendarX2, Lock, QrCode, Zap } from "lucide-react";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState, Loading } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ExtraEventPix from "@/components/ExtraEventPix";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "feed", label: "Todos os Eventos", scope: "feed" },
  { key: "applied", label: "Que me Candidatei", scope: "applied" },
  { key: "mine", label: "Meus Eventos Criados", scope: "mine" },
];

export default function Events() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "feed");
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [showPix, setShowPix] = useState(false);
  const [originFilter, setOriginFilter] = useState("all");
  const navigate = useNavigate();
  const { user } = useAuth();

  const loadUsage = () => api.get("/me/plan").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => { loadUsage(); }, []);

  useEffect(() => {
    const scope = TABS.find((t) => t.key === tab)?.scope || "feed";
    setLoading(true);
    api.get(`/events?scope=${scope}`).then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, [tab]);

  const canCreate = !usage || usage.can_create;
  const filtered = events.filter((e) => (e.name.toLowerCase().includes(q.toLowerCase()) || e.address.toLowerCase().includes(q.toLowerCase()))
    && (tab !== "mine" || originFilter === "all" || (originFilter === "express" ? e.origin === "EM_CIMA_DA_HORA" : e.origin !== "EM_CIMA_DA_HORA")));

  return (
    <div className="space-y-6" data-testid="events-page">
      <PageHeader
        title="Eventos"
        description={tab === "feed" ? "Oportunidades compatíveis com seus instrumentos e agenda, ordenadas por proximidade." : tab === "applied" ? "Todas as suas candidaturas e seus status." : "Eventos que você criou."}
        action={
          <div className="flex gap-2">
            {(user?.user_type === "creator" || user?.user_type === "both") && (
              <Button data-testid="express-entry-btn" onClick={() => navigate("/em-cima-da-hora")} size="lg" variant="outline" className="gap-2 bg-transparent border-[#FF4D00]/40 text-[#FF4D00] hover:bg-[#FF4D00]/10">
                <Zap className="h-5 w-5" /> Em cima da Hora
              </Button>
            )}
            {canCreate ? (
              <Button data-testid="new-event-btn" onClick={() => navigate("/eventos/novo")} size="lg" className="gap-2">
                <PlusCircle className="h-5 w-5" /> Criar evento
              </Button>
            ) : (
              <Button data-testid="new-event-blocked-btn" onClick={() => setShowPix(true)} size="lg" variant="outline" className="gap-2 bg-transparent border-[#FF4D00]/40 text-[#FF4D00]">
                <Lock className="h-4 w-4" /> Criar evento
              </Button>
            )}
          </div>
        }
      />

      <div className="flex items-center gap-4 flex-wrap">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-surface border border-border flex-wrap h-auto">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} data-testid={`tab-${t.key}`} value={t.key} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="event-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou local..." className="pl-9 bg-surface border-border rounded-md" />
        </div>
        {usage && !usage.unlimited && (
          <div data-testid="event-usage" className="text-xs text-muted-foreground">
            {usage.can_create ? (
              <>
                {usage.remaining > 0 ? (
                  <><span className="font-semibold text-foreground">{usage.remaining}</span> evento(s) disponível(is) · usados <span className="font-semibold text-foreground">{usage.used}</span> de <span className="font-semibold text-foreground">{usage.effective_limit ?? usage.limit}</span> {usage.lifetime ? "(plano gratuito)" : "neste ciclo"}</>
                ) : usage.first_event_available ? (
                  <span className="text-foreground font-semibold">1 evento inicial disponível</span>
                ) : null}
                {usage.event_credits > 0 && <span className="ml-2 px-2 py-0.5 rounded-sm bg-emerald-500/10 text-success border border-emerald-500/30">+{usage.event_credits} avulso(s) disponível(is)</span>}
              </>
            ) : (
              <span className="inline-flex items-center gap-1 text-[#FF4D00] font-semibold"><Lock className="h-3 w-3" />Limite atingido — usados {usage.used} de {usage.effective_limit ?? usage.limit}</span>
            )}
          </div>
        )}
      </div>

      {tab === "mine" && (
        <div className="flex gap-1 p-1 rounded-xl bg-surface border border-border w-fit" data-testid="origin-filter">
          {[["all", "Todos"], ["normal", "Eventos"], ["express", "⚡ Em cima da Hora"]].map(([v, l]) => (
            <button key={v} data-testid={`origin-filter-${v}`} onClick={() => setOriginFilter(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${originFilter === v ? "bg-[#FF4D00] text-white" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
      )}

      {loading ? <Loading /> : filtered.length === 0 ? (
        <EmptyState icon={CalendarX2}
          description={tab === "feed" ? "Nenhuma oportunidade compatível no momento. Cadastre seus instrumentos e localização no perfil para ver mais." : "Nenhum evento aqui ainda."} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{filtered.map((ev, i) => <EventCard key={ev.id} ev={ev} index={i} />)}</div>
      )}

      <Dialog open={showPix} onOpenChange={setShowPix}>
        <DialogContent data-testid="events-extra-dialog" className="bg-[#121217] border-border">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-[#FF4D00]" />Limite de eventos atingido</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400">Você atingiu o limite de eventos disponível para sua conta. Escolha uma opção para continuar:</p>
          <div className="rounded-md border border-border bg-[#09090B] p-4 space-y-3">
            <div className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-2"><QrCode className="h-4 w-4 text-[#FF4D00]" />Comprar evento avulso (Pix)</div>
            <ExtraEventPix fee={usage?.event_extra_fee} onCredited={() => { loadUsage(); setTimeout(() => { setShowPix(false); navigate("/eventos/novo"); }, 900); }} />
          </div>
          <button data-testid="events-upgrade-btn" onClick={() => { setShowPix(false); navigate("/meu-plano"); }} className="w-full mt-1 rounded-md border border-[#FF4D00]/40 text-[#FF4D00] py-2.5 text-sm font-medium hover:bg-[#FF4D00]/10 transition-colors">Fazer upgrade de plano</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
